import { useCallback, useEffect, useRef, useState } from "react"

export type CameraStatus = "requesting" | "active" | "denied" | "unavailable"

const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: { ideal: "environment" } },
  audio: false,
}

const PERMISSION_DENIED_ERRORS = ["NotAllowedError", "SecurityError"]

const statusForError = (error: unknown): CameraStatus =>
  error instanceof DOMException && PERMISSION_DENIED_ERRORS.includes(error.name)
    ? "denied"
    : "unavailable"

const stopStream = (stream: MediaStream | null) =>
  stream?.getTracks().forEach((track) => track.stop())

/**
 * Opens the rear camera into the returned video ref. When the patient denies access,
 * `status` becomes "denied" and we retry as soon as the browser reports the permission
 * changed or the page becomes visible again (the patient coming back from their settings).
 */
export const useCameraStream = () => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [status, setStatus] = useState<CameraStatus>("requesting")
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => {
    setStatus("requesting")
    setAttempt((current) => current + 1)
  }, [])

  useEffect(() => {
    const video = videoRef.current
    let cancelled = false
    let stream: MediaStream | null = null

    const open = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS)
        if (cancelled) {
          stopStream(mediaStream)
          return
        }
        stream = mediaStream
        if (video) {
          video.srcObject = mediaStream
          await video.play()
        }
        setStatus("active")
      } catch (error) {
        if (!cancelled) setStatus(statusForError(error))
      }
    }

    open()
    return () => {
      cancelled = true
      stopStream(stream)
      if (video) video.srcObject = null
    }
  }, [attempt])

  useEffect(() => {
    if (status !== "denied") return
    let cancelled = false
    let permissionStatus: PermissionStatus | null = null

    const retryWhenVisible = () => {
      if (document.visibilityState === "visible") retry()
    }
    const retryWhenGranted = () => {
      if (permissionStatus?.state === "granted") retry()
    }

    document.addEventListener("visibilitychange", retryWhenVisible)

    navigator.permissions
      ?.query({ name: "camera" as PermissionName })
      .then((result) => {
        if (cancelled) return
        permissionStatus = result
        result.addEventListener("change", retryWhenGranted)
      })
      .catch(() => {})

    return () => {
      cancelled = true
      document.removeEventListener("visibilitychange", retryWhenVisible)
      permissionStatus?.removeEventListener("change", retryWhenGranted)
    }
  }, [status, retry])

  return { videoRef, status, retry }
}
